/**
 * BIOMES.js - World Map Biome Definitions
 *
 * NOTE: EXPEDITION config has been moved to balance.js
 * NOTE: Dungeon biomes are defined in dungeonBiomes.js
 *
 * RESOURCE ID MAPPING (valid IDs from resources.js):
 * - Tier 1: wood, stone, food
 * - Tier 2: gold, iron, population
 * - Tier 3: knowledge, culture
 * - Tier 4: steel, coal, gems
 * - Tier 5: tools, machinery
 * - Tier 6: science, energy
 * - Tier 7: magic, artifacts, crystals
 */

import DUNGEON_BIOMES from './dungeonBiomes';
import { ENCOUNTER_BIOMES } from './encounterBiomes';
import { TOWER_BIOMES } from './towerBiomes';
import { SHRINE_BIOMES } from './shrineBiomes';
import { KINGDOM_TYPES } from './kingdoms';

// ============================================================================
// ORIGINAL CORE BIOMES (with building associations)
// ============================================================================

const CORE_BIOMES = {
  forest: {
    id: 'forest',
    name: 'Forest',
    icon: '🌲',
    description: 'Dense woodland rich with timber and wild game.',
    buildingCategory: 'Wood & Nature',
    associatedBuildings: ['woodcutter', 'forager', 'hunter', 'gathererCamp', 'stonePicker', 'skinTrader'],
    color: '#4a7048',
    minAge: 1,
    spawnWeight: 30,
    entryEvents: [
      { id: 'peaceful', weight: 50, text: 'The forest is peaceful. Birds sing in the canopy above.' },
      { id: 'berries', weight: 20, text: 'You found a bush of ripe berries!', rewards: { food: 5 } },
      { id: 'herbs', weight: 15, text: 'Medicinal herbs grow near a stream.', rewards: { food: 3 } },
      { id: 'old_cache', weight: 10, text: 'An old hunter\'s cache! Some supplies remain.', rewards: { wood: 10, food: 5 } },
      { id: 'wolves', weight: 5, text: 'Wolves stalked you through the trees. You escaped, but dropped some supplies.', penalties: { food: 3 } },
    ],
    gatherableResources: {
      wood: { baseAmount: 5, richness: 1.0 },
      food: { baseAmount: 3, richness: 0.6 },
    },
    passiveIncome: { wood: 2, food: 1 },
  },

  plains: {
    id: 'plains',
    name: 'Plains',
    icon: '🌾',
    description: 'Fertile grasslands perfect for agriculture and settlement.',
    buildingCategory: 'Settlement & Agriculture',
    associatedBuildings: ['gathererCamp', 'farm', 'windmill', 'granary', 'house', 'cottage', 'homestead', 'barracks', 'hospital', 'theater', 'mansion', 'palace', 'fortress', 'grandCastle', 'market', 'tradingPost', 'bank', 'workshop', 'factory'],
    color: '#8a7a5a',
    minAge: 1,
    spawnWeight: 30,
    entryEvents: [
      { id: 'peaceful', weight: 50, text: 'Rolling fields of golden grass stretch to the horizon.' },
      { id: 'wild_wheat', weight: 25, text: 'Wild wheat grows abundantly here!', rewards: { food: 8 } },
      { id: 'travelers', weight: 15, text: 'Friendly travelers share their provisions.', rewards: { food: 5, gold: 2 } },
      { id: 'locusts', weight: 10, text: 'A swarm of locusts descends! Some of your food stores are ruined.', penalties: { food: 5 } },
    ],
    gatherableResources: { food: { baseAmount: 6, richness: 1.2 } },
    passiveIncome: { food: 3 },
  },

  mountains: {
    id: 'mountains',
    name: 'Mountains',
    icon: '⛰️',
    description: 'Rugged peaks hiding valuable ore deposits and precious gems.',
    buildingCategory: 'Mining & Extraction',
    associatedBuildings: ['stonePicker', 'hunter', 'skinTrader', 'quarry', 'stonePit', 'prospectorCamp', 'mine', 'deepMine', 'goldMine', 'gemMine', 'smelter', 'forge', 'coalMine'],
    color: '#6a6560',
    minAge: 2,
    spawnWeight: 20,
    entryEvents: [
      { id: 'peaceful', weight: 45, text: 'The mountain air is crisp and clear. Peaks tower above.' },
      { id: 'ore_vein', weight: 20, text: 'You spot a rich ore vein glinting in the rock!', rewards: { iron: 5, stone: 5 } },
      { id: 'gem_find', weight: 10, text: 'A glittering gem catches your eye!', rewards: { gems: 1 } },
      { id: 'rockslide', weight: 15, text: 'A rockslide blocks your path! You lose time and supplies.', penalties: { food: 3 } },
      { id: 'cave_cache', weight: 10, text: 'An abandoned mine shaft contains old supplies.', rewards: { stone: 10, iron: 3 } },
    ],
    gatherableResources: {
      stone: { baseAmount: 4, richness: 1.0 },
      iron: { baseAmount: 2, richness: 0.7 },
    },
    passiveIncome: { stone: 2, iron: 1 },
  },

  coastal: {
    id: 'coastal',
    name: 'Coastal',
    icon: '🏖️',
    description: 'Shorelines offering access to maritime trade routes.',
    buildingCategory: 'Trade & Commerce',
    associatedBuildings: ['market', 'tradingPost', 'harbor', 'bank'],
    color: '#5a8a98',
    minAge: 2,
    spawnWeight: 15,
    entryEvents: [
      { id: 'peaceful', weight: 45, text: 'Waves lap gently at the shore. Seabirds cry overhead.' },
      { id: 'shipwreck', weight: 20, text: 'Wreckage from an old ship washes ashore!', rewards: { gold: 8, wood: 5 } },
      { id: 'pearl', weight: 15, text: 'You find an oyster with a pearl inside!', rewards: { gold: 5 } },
      { id: 'fishing', weight: 15, text: 'The waters teem with fish!', rewards: { food: 10 } },
      { id: 'storm', weight: 5, text: 'A sudden squall drenches your supplies!', penalties: { food: 4 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 3, richness: 0.8 },
      food: { baseAmount: 3, richness: 0.6 },
    },
    passiveIncome: { gold: 1, food: 1 },
  },

  ruins: {
    id: 'ruins',
    name: 'Ancient Ruins',
    icon: '🏚️',
    description: 'Crumbling remains of a lost civilization, filled with forgotten knowledge.',
    buildingCategory: 'Knowledge & Culture',
    associatedBuildings: ['library', 'scriptorium', 'theater', 'academy', 'university', 'museum', 'temple', 'cathedral', 'observatory', 'laboratory', 'researchCenter', 'eternalLibrary', 'wonder'],
    color: '#7a7368',
    minAge: 4,
    spawnWeight: 12,
    entryEvents: [
      { id: 'peaceful', weight: 35, text: 'Ancient stones whisper of ages past. The air feels heavy with history.' },
      { id: 'scrolls', weight: 25, text: 'You discover ancient scrolls preserved in a sealed chamber!', rewards: { knowledge: 15 } },
      { id: 'treasure', weight: 15, text: 'A hidden vault contains forgotten riches!', rewards: { gold: 12, gems: 1 } },
      { id: 'trap', weight: 15, text: 'An ancient trap springs! You barely escape.', penalties: { food: 5 } },
      { id: 'curse', weight: 10, text: 'You disturb something best left alone. An ill wind follows you.', penalties: { gold: 5, food: 3 } },
    ],
    gatherableResources: { knowledge: { baseAmount: 4, richness: 1.5 } },
    passiveIncome: { knowledge: 2 },
  },

  volcanic: {
    id: 'volcanic',
    name: 'Volcanic',
    icon: '🌋',
    description: 'Dangerous volcanic terrain offering extreme heat for industry.',
    buildingCategory: 'Industry & Crafting',
    associatedBuildings: ['forge', 'workshop', 'steelMill', 'factory', 'powerPlant', 'artifactForge'],
    color: '#8a4540',
    minAge: 6,
    spawnWeight: 10,
    entryEvents: [
      { id: 'heat', weight: 35, text: 'The air shimmers with intense heat. Lava flows in the distance.' },
      { id: 'obsidian', weight: 20, text: 'Volcanic glass glitters among the rocks!', rewards: { stone: 8, iron: 5 } },
      { id: 'coal_vein', weight: 20, text: 'Rich coal deposits line the crater walls.', rewards: { coal: 10 } },
      { id: 'eruption', weight: 15, text: 'A minor eruption! Flee before the lava reaches you!', penalties: { food: 6, wood: 5 } },
      { id: 'gas', weight: 10, text: 'Toxic fumes overwhelm you briefly. You stagger away weakened.', penalties: { food: 8 } },
    ],
    gatherableResources: {
      iron: { baseAmount: 5, richness: 1.3 },
      coal: { baseAmount: 4, richness: 1.0 },
    },
    passiveIncome: { iron: 2, coal: 2 },
  },

  mysticGrove: {
    id: 'mysticGrove',
    name: 'Mystic Grove',
    icon: '✨',
    description: 'An enchanted forest pulsing with raw magical energy.',
    buildingCategory: 'Magic & Enchantment',
    associatedBuildings: ['wizardTower', 'alchemyLab', 'enchanter', 'sanctum', 'arcaneNexus'],
    color: '#7060a0',
    minAge: 8,
    spawnWeight: 8,
    entryEvents: [
      { id: 'peaceful', weight: 30, text: 'The trees shimmer with ethereal light. Magic hums in the air.' },
      { id: 'magic_well', weight: 25, text: 'A wellspring of pure magic flows from the earth!', rewards: { magic: 15 } },
      { id: 'crystal', weight: 20, text: 'A cluster of magical crystals grows from an ancient stump.', rewards: { crystals: 3 } },
      { id: 'fairy', weight: 15, text: 'Playful sprites lead you to a hidden cache!', rewards: { magic: 8, gold: 5 } },
      { id: 'illusion', weight: 10, text: 'You wander lost in magical illusions, wasting precious time.', penalties: { food: 5 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 3, richness: 1.0 },
      crystals: { baseAmount: 1, richness: 0.5 },
    },
    passiveIncome: { magic: 1 },
  },

  barrenWastes: {
    id: 'barrenWastes',
    name: 'Barren Wastes',
    icon: '💨',
    description: 'Nothing but dust and wind. A desolate stretch of land.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4540',
    minAge: 1,
    spawnWeight: 18,
    entryEvents: [
      { id: 'desolate', weight: 40, text: 'Nothing but dust and despair. The wind howls mournfully.' },
      { id: 'sandstorm', weight: 25, text: 'A sandstorm engulfs you! You lose supplies in the chaos.', penalties: { food: 5, wood: 3 } },
      { id: 'lost', weight: 20, text: 'You wander in circles, exhausted and confused.', penalties: { food: 4 } },
      { id: 'thirst', weight: 15, text: 'The scorching heat drains your water reserves.', penalties: { food: 6 } },
    ],
    gatherableResources: null,
    passiveIncome: null,
    canBuildOutpost: false,
  },

  rockyOutcrop: {
    id: 'rockyOutcrop',
    name: 'Rocky Outcrop',
    icon: '🗻',
    description: 'Exposed bedrock with sparse vegetation. Some stone can be chipped away.',
    buildingCategory: 'Mining & Extraction',
    associatedBuildings: ['stonePicker', 'quarry', 'stonePit', 'mine', 'smelter'],
    color: '#5a5550',
    minAge: 1,
    spawnWeight: 12,
    entryEvents: [
      { id: 'barren', weight: 50, text: 'Bare rock stretches before you. Not much here.' },
      { id: 'flint', weight: 30, text: 'You find some useful flint among the rocks.', rewards: { stone: 4 } },
      { id: 'twisted_ankle', weight: 20, text: 'The uneven terrain causes you to stumble.', penalties: { food: 2 } },
    ],
    gatherableResources: { stone: { baseAmount: 2, richness: 0.5 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  deadForest: {
    id: 'deadForest',
    name: 'Dead Forest',
    icon: '🥀',
    description: 'Withered trees and fallen logs. Little life remains here.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3d3530',
    minAge: 1,
    spawnWeight: 10,
    entryEvents: [
      { id: 'eerie', weight: 40, text: 'Dead trees creak in the wind. An eerie silence hangs in the air.' },
      { id: 'firewood', weight: 30, text: 'Dry branches make excellent firewood.', rewards: { wood: 4 } },
      { id: 'spooked', weight: 20, text: 'Strange shadows spook you. You flee, dropping supplies.', penalties: { food: 3 } },
      { id: 'bones', weight: 10, text: 'You find old bones... and a pouch of coins nearby.', rewards: { gold: 5 } },
    ],
    gatherableResources: { wood: { baseAmount: 1, richness: 0.3 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  grassland: {
    id: 'grassland',
    name: 'Grassland',
    icon: '🌿',
    description: 'Rolling hills of tall grass. Good for passing through.',
    buildingCategory: 'Gathering',
    associatedBuildings: ['gathererCamp'],
    color: '#5a7048',
    minAge: 1,
    spawnWeight: 15,
    entryEvents: [
      { id: 'peaceful', weight: 50, text: 'Gentle hills roll into the distance. A pleasant breeze blows.' },
      { id: 'rabbits', weight: 25, text: 'You catch some rabbits for the pot!', rewards: { food: 5 } },
      { id: 'herbs', weight: 20, text: 'Wild herbs grow among the grass.', rewards: { food: 3 } },
      { id: 'nothing', weight: 5, text: 'Just grass as far as the eye can see. Unremarkable.' },
    ],
    gatherableResources: { food: { baseAmount: 2, richness: 0.4 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  overgrownShrine: {
    id: 'overgrownShrine',
    name: 'Overgrown Shrine',
    icon: '🗿',
    description: 'A small, moss-covered shrine from a forgotten time. Faded inscriptions hint at old wisdom.',
    buildingCategory: 'Knowledge & Culture',
    associatedBuildings: ['library', 'scriptorium'],
    color: '#5a6a50',
    minAge: 1,
    spawnWeight: 10,
    entryEvents: [
      { id: 'peaceful', weight: 40, text: 'Vines crawl over weathered stone. A sense of quiet reverence fills the air.' },
      { id: 'inscription', weight: 25, text: 'You decipher a faded inscription. Simple wisdom, but wisdom nonetheless.', rewards: { knowledge: 2 } },
      { id: 'offering', weight: 15, text: 'An old offering bowl contains a few forgotten coins.', rewards: { gold: 3, knowledge: 1 } },
      { id: 'meditation', weight: 15, text: 'You rest and reflect. The quiet clears your mind.', rewards: { knowledge: 1, food: 2 } },
      { id: 'crumble', weight: 5, text: 'Part of the shrine crumbles as you explore. Nothing of value here.' },
    ],
    gatherableResources: { knowledge: { baseAmount: 1, richness: 0.2 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  shallowWaters: {
    id: 'shallowWaters',
    name: 'Shallow Waters',
    icon: '🌊',
    description: 'Calm coastal waters teeming with fish.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a7080',
    minAge: 3,
    spawnWeight: 8,
    requiresTech: 'sailing',
    entryEvents: [
      { id: 'calm', weight: 40, text: 'Crystal clear waters lap gently. Fish dart beneath the surface.' },
      { id: 'fishing', weight: 30, text: 'The waters are rich with fish!', rewards: { food: 8 } },
      { id: 'shells', weight: 20, text: 'Beautiful shells litter the shallows. Some contain pearls!', rewards: { gold: 4 } },
      { id: 'riptide', weight: 10, text: 'A sudden current pulls at you. You escape but lose some gear.', penalties: { food: 3 } },
    ],
    gatherableResources: {
      food: { baseAmount: 4, richness: 0.8 },
      gold: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  ancientRuins: {
    id: 'ancientRuins',
    name: 'Ancient Ruins',
    icon: '🏛️',
    description: 'Crumbling remnants of a forgotten civilization. Secrets await discovery.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6a6055',
    minAge: 4,
    spawnWeight: 5,
    requiresTech: 'archaeology',
    entryEvents: [
      { id: 'wonder', weight: 30, text: 'Massive stone pillars tower overhead. What civilization built this?' },
      { id: 'artifacts', weight: 25, text: 'Ancient artifacts lie scattered among the rubble!', rewards: { knowledge: 10, gold: 8 } },
      { id: 'secret_chamber', weight: 20, text: 'You discover a hidden chamber filled with treasures!', rewards: { gold: 15, gems: 2 } },
      { id: 'guardian', weight: 15, text: 'Ancient guardians still protect this place! You flee.', penalties: { food: 5 } },
      { id: 'collapse', weight: 10, text: 'The unstable ruins collapse around you!', penalties: { food: 6, gold: 3 } },
    ],
    gatherableResources: {
      knowledge: { baseAmount: 5, richness: 1.2 },
      gold: { baseAmount: 3, richness: 0.6 },
      gems: { baseAmount: 1, richness: 0.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  swamp: {
    id: 'swamp',
    name: 'Swamp',
    icon: '🐊',
    description: 'Murky waters and twisted trees. Dangerous but resource-rich.',
    buildingCategory: 'Gathering',
    associatedBuildings: ['forager'],
    color: '#3a4a35',
    minAge: 2,
    spawnWeight: 8,
    entryEvents: [
      { id: 'murky', weight: 35, text: 'Murky water and twisted trees. The air is thick and humid.' },
      { id: 'mushrooms', weight: 25, text: 'Edible mushrooms grow on rotting logs.', rewards: { food: 5 } },
      { id: 'bog_iron', weight: 15, text: 'You find bog iron deposits in the muck!', rewards: { iron: 3 } },
      { id: 'croc', weight: 15, text: 'A crocodile lunges from the water! You escape but lose supplies.', penalties: { food: 5 } },
      { id: 'quicksand', weight: 10, text: 'Quicksand! You struggle free but lose some gear.', penalties: { wood: 3, food: 2 } },
    ],
    gatherableResources: {
      food: { baseAmount: 2, richness: 0.4 },
      wood: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  frozenTundra: {
    id: 'frozenTundra',
    name: 'Frozen Tundra',
    icon: '❄️',
    description: 'Permafrost as far as the eye can see. Harsh but sometimes rewarding.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6080a0',
    minAge: 3,
    spawnWeight: 6,
    entryEvents: [
      { id: 'cold', weight: 35, text: 'Bitter cold bites at your skin. Ice stretches endlessly.' },
      { id: 'mammoth', weight: 20, text: 'Frozen mammoth remains yield valuable ivory!', rewards: { gold: 8 } },
      { id: 'ice_cave', weight: 20, text: 'An ice cave shelters ancient preserved supplies.', rewards: { food: 6, stone: 4 } },
      { id: 'frostbite', weight: 15, text: 'The cold is unbearable. You must turn back.', penalties: { food: 5 } },
      { id: 'blizzard', weight: 10, text: 'A blizzard strikes! You hunker down and lose supplies.', penalties: { food: 7, wood: 3 } },
    ],
    gatherableResources: { stone: { baseAmount: 3, richness: 0.6 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  voidRift: {
    id: 'voidRift',
    name: 'Void Rift',
    icon: '🌀',
    description: 'A tear in reality leaking cosmic energy from beyond the stars.',
    buildingCategory: 'Dimensional',
    associatedBuildings: ['dimensionalBeacon'],
    color: '#3a3050',
    minAge: 10,
    spawnWeight: 5,
    entryEvents: [
      { id: 'whispers', weight: 25, text: 'Reality bends around you. Voices whisper from beyond the veil.' },
      { id: 'energy', weight: 25, text: 'Pure cosmic energy pours through the rift!', rewards: { energy: 10, magic: 10 } },
      { id: 'vision', weight: 20, text: 'A vision floods your mind. Knowledge flows.', rewards: { knowledge: 20, energy: 5 } },
      { id: 'void_touched', weight: 15, text: 'The void touches your soul. Power... at a cost.', rewards: { magic: 15 }, penalties: { food: 10 } },
      { id: 'unraveling', weight: 15, text: 'Reality unravels around you! Flee before you cease to exist!', penalties: { gold: 15, food: 8 } },
    ],
    gatherableResources: { energy: { baseAmount: 2, richness: 0.8 } },
    passiveIncome: { energy: 1 },
  },

  teleportBeacon: {
    id: 'teleportBeacon',
    name: 'Ancient Waystone',
    icon: '🔮',
    description: 'A mysterious waystone from a forgotten age. You can teleport here from anywhere on the map.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6a50a0',
    minAge: 1,
    spawnWeight: 3,
    isTeleportBeacon: true,
    entryEvents: [
      { id: 'activated', weight: 60, text: 'The waystone hums with ancient power. You can now teleport here at will.' },
      { id: 'memory', weight: 25, text: 'Visions of travelers long past flash before your eyes.', rewards: { knowledge: 8 } },
      { id: 'blessing', weight: 15, text: 'The waystone bestows a blessing upon you!', rewards: { magic: 5, gold: 10 } },
    ],
    gatherableResources: { magic: { baseAmount: 2, richness: 0.5 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },
};

// ============================================================================
// EXPANDED BIOMES
// ============================================================================

const EXPANDED_BIOMES = {
  meadow: {
    id: 'meadow',
    name: 'Sunlit Meadow',
    icon: '🌻',
    description: 'A peaceful meadow bursting with wildflowers and buzzing with life.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#7a9a48',
    minAge: 1,
    spawnWeight: 20,
    entryEvents: [
      { id: 'peaceful', weight: 35, text: 'Butterflies dance among the wildflowers. A gentle breeze carries the scent of honey.' },
      { id: 'beehive', weight: 25, text: 'You discover a wild beehive! Sweet honey for your stores.', rewards: { food: 8 } },
      { id: 'herbs', weight: 20, text: 'Medicinal herbs grow in abundance here.', rewards: { food: 5 } },
      { id: 'snake', weight: 12, text: 'A venomous snake strikes from the tall grass! You escape but drop your pack.', penalties: { food: 4 } },
      { id: 'fairy_ring', weight: 8, text: 'A ring of mushrooms pulses with faint light. You feel... watched.', rewards: { food: 3, magic: 1 } },
    ],
    gatherableResources: { food: { baseAmount: 4, richness: 0.8 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  riverDelta: {
    id: 'riverDelta',
    name: 'River Delta',
    icon: '🏞️',
    description: 'Where mighty rivers meet the sea. Rich silt makes for fertile ground.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a8070',
    minAge: 1,
    spawnWeight: 12,
    entryEvents: [
      { id: 'fertile', weight: 30, text: 'Dark, rich soil stretches before you. Fish leap in the shallows.' },
      { id: 'fishing', weight: 25, text: 'The waters teem with fish! An easy catch.', rewards: { food: 10 } },
      { id: 'clay', weight: 20, text: 'Fine clay deposits line the riverbank.', rewards: { stone: 4 } },
      { id: 'flood', weight: 15, text: 'Flash flood! You scramble to higher ground, losing supplies.', penalties: { food: 5, wood: 3 } },
      { id: 'crocodile', weight: 10, text: 'River crocodiles lurk beneath the murky surface. One lunges!', penalties: { food: 6 } },
    ],
    gatherableResources: {
      food: { baseAmount: 5, richness: 1.0 },
      stone: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  bambooGrove: {
    id: 'bambooGrove',
    name: 'Bamboo Grove',
    icon: '🎋',
    description: 'Towering bamboo stalks sway and creak in the wind. Fast-growing and versatile.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a8048',
    minAge: 1,
    spawnWeight: 10,
    entryEvents: [
      { id: 'serene', weight: 35, text: 'Bamboo stalks click together like wind chimes. The grove is peaceful.' },
      { id: 'harvest', weight: 30, text: 'Young bamboo shoots make excellent building material!', rewards: { wood: 8 } },
      { id: 'panda', weight: 15, text: 'A rare panda watches you from the shadows. It seems... friendly?', rewards: { food: 3 } },
      { id: 'lost', weight: 12, text: 'The identical stalks confuse you. Hours pass before you find your way.', penalties: { food: 3 } },
      { id: 'spirit', weight: 8, text: 'An ancient spirit dwells here. It offers wisdom in exchange for respect.', rewards: { knowledge: 5 } },
    ],
    gatherableResources: { wood: { baseAmount: 6, richness: 1.2 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  savanna: {
    id: 'savanna',
    name: 'Golden Savanna',
    icon: '🦁',
    description: 'Endless golden grasslands dotted with acacia trees. Herds roam freely.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#a08040',
    minAge: 1,
    spawnWeight: 15,
    entryEvents: [
      { id: 'vast', weight: 30, text: 'The savanna stretches endlessly. Distant herds kick up dust on the horizon.' },
      { id: 'hunt', weight: 25, text: 'A successful hunt! The herds here are plentiful.', rewards: { food: 12 } },
      { id: 'watering_hole', weight: 15, text: 'You find a watering hole. Animals gather peacefully.', rewards: { food: 6 } },
      { id: 'lion', weight: 18, text: 'Lions stalk your party! You escape but abandon supplies.', penalties: { food: 7 } },
      { id: 'stampede', weight: 12, text: 'A wildebeest stampede! You dive aside as the thunder passes.', penalties: { food: 5, wood: 2 } },
    ],
    gatherableResources: {
      food: { baseAmount: 5, richness: 1.0 },
      wood: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  thornlands: {
    id: 'thornlands',
    name: 'The Thornlands',
    icon: '🌵',
    description: 'A brutal landscape of razor-sharp thorns and hardy survivors.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a4030',
    minAge: 1,
    spawnWeight: 8,
    entryEvents: [
      { id: 'hostile', weight: 25, text: 'Thorns tear at your clothes. Every step is a battle.' },
      { id: 'cactus_fruit', weight: 20, text: 'Cactus fruit! Sweet water hidden behind spines.', rewards: { food: 7 } },
      { id: 'trapped_trader', weight: 15, text: 'A trapped merchant rewards you for rescue!', rewards: { gold: 8, food: 3 } },
      { id: 'cuts', weight: 25, text: 'Thorns slice through your pack. Supplies spill and are lost.', penalties: { food: 5, wood: 2 } },
      { id: 'venom', weight: 15, text: 'A thorn-spider bites! The venom weakens you for days.', penalties: { food: 8 } },
    ],
    gatherableResources: {
      food: { baseAmount: 2, richness: 0.4 },
      wood: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  mushroomCavern: {
    id: 'mushroomCavern',
    name: 'Mushroom Cavern',
    icon: '🍄',
    description: 'A vast underground cave lit by bioluminescent fungi.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a3060',
    minAge: 1,
    spawnWeight: 6,
    entryEvents: [
      { id: 'wonder', weight: 30, text: 'Glowing mushrooms illuminate a hidden world.' },
      { id: 'edible', weight: 25, text: 'Vast fields of edible mushrooms! A feast underground.', rewards: { food: 10 } },
      { id: 'crystals', weight: 15, text: 'Crystals grow among the fungi, catching the eerie light.', rewards: { gems: 1 } },
      { id: 'hallucinogenic', weight: 18, text: 'You accidentally inhale spores. Hours pass in confused wandering.', penalties: { food: 4 } },
      { id: 'cave_in', weight: 12, text: 'The ceiling groans ominously. You flee as rocks fall!', penalties: { food: 5, stone: 2 } },
    ],
    gatherableResources: { food: { baseAmount: 4, richness: 0.8 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  oasis: {
    id: 'oasis',
    name: 'Hidden Oasis',
    icon: '🏝️',
    description: 'A miraculous spring in the desert. Life clusters around precious water.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#40a080',
    minAge: 1,
    spawnWeight: 5,
    entryEvents: [
      { id: 'paradise', weight: 35, text: 'Palm trees sway over crystal waters. A true paradise.' },
      { id: 'rest', weight: 30, text: 'You rest and resupply at the life-giving waters.', rewards: { food: 12 } },
      { id: 'traders', weight: 20, text: 'Desert traders share the oasis. They offer fair trades!', rewards: { gold: 6, food: 4 } },
      { id: 'contested', weight: 10, text: 'Others claim this oasis! A tense standoff costs supplies.', penalties: { food: 4, gold: 3 } },
      { id: 'mirage', weight: 5, text: 'Wait... the oasis flickers. A mirage!', penalties: { food: 8 } },
    ],
    gatherableResources: {
      food: { baseAmount: 6, richness: 1.2 },
      gold: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  leprechaunHollow: {
    id: 'leprechaunHollow',
    name: 'Leprechaun Hollow',
    icon: '🍀',
    description: 'A mystical glen where fairy folk hide their treasures. Gold coins glint beneath clover patches.',
    buildingCategory: 'Trade & Commerce',
    associatedBuildings: ['market', 'tradingPost', 'skinTrader'],
    color: '#3a8a4a',
    minAge: 1,
    spawnWeight: 8,
    entryEvents: [
      { id: 'lucky', weight: 30, text: 'Four-leaf clovers carpet the ground. You feel strangely lucky.' },
      { id: 'pot_of_gold', weight: 20, text: 'A small pot of gold coins sits at the base of a rainbow!', rewards: { gold: 12 } },
      { id: 'fairy_trade', weight: 20, text: 'Tiny folk emerge and offer a trade. Fair dealing brings fortune!', rewards: { gold: 8, food: 4 } },
      { id: 'trickster', weight: 15, text: 'A mischievous sprite leads you in circles. You lose time but find coins.', rewards: { gold: 4 }, penalties: { food: 3 } },
      { id: 'cursed_gold', weight: 10, text: 'You took without asking! The gold turns to leaves in your hands.', penalties: { gold: 5 } },
      { id: 'blessing', weight: 5, text: 'The fairy queen blesses your journey with abundant fortune!', rewards: { gold: 20, food: 6 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 4, richness: 1.0 },
      food: { baseAmount: 2, richness: 0.5 },
    },
    passiveIncome: { gold: 1 },
    canBuildOutpost: true,
  },

  crystalCaves: {
    id: 'crystalCaves',
    name: 'Crystal Caves',
    icon: '💎',
    description: 'Caverns encrusted with glittering crystals of every hue.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6080a0',
    minAge: 2,
    spawnWeight: 8,
    entryEvents: [
      { id: 'dazzling', weight: 25, text: 'Light refracts through countless crystals. The cave sparkles like stars.' },
      { id: 'harvest', weight: 25, text: 'You carefully extract valuable crystal formations!', rewards: { gems: 2, stone: 5 } },
      { id: 'rare_find', weight: 15, text: 'A massive crystal cluster! This will fetch a fortune.', rewards: { gems: 4, gold: 8 } },
      { id: 'sharp', weight: 20, text: 'Crystal shards slice through your pack. Supplies scatter.', penalties: { food: 4, gems: 1 } },
      { id: 'echo', weight: 15, text: 'Strange harmonics from the crystals disorient you.', penalties: { food: 5 } },
    ],
    gatherableResources: {
      gems: { baseAmount: 2, richness: 0.8 },
      stone: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  sulfurSprings: {
    id: 'sulfurSprings',
    name: 'Sulfur Springs',
    icon: '♨️',
    description: 'Bubbling hot springs reeking of brimstone.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#a0a040',
    minAge: 2,
    spawnWeight: 7,
    entryEvents: [
      { id: 'stench', weight: 30, text: 'The smell of rotten eggs assaults your senses.' },
      { id: 'sulfur', weight: 30, text: 'Pure sulfur deposits! Valuable for industry.', rewards: { coal: 8, gold: 4 } },
      { id: 'hot_spring', weight: 15, text: 'A natural hot spring soothes your aching muscles.', rewards: { food: 5 } },
      { id: 'geyser', weight: 15, text: 'A geyser erupts beneath you! Scalding water burns.', penalties: { food: 6 } },
      { id: 'fumes', weight: 10, text: 'Toxic fumes overwhelm you.', penalties: { food: 8, gold: 3 } },
    ],
    gatherableResources: {
      coal: { baseAmount: 4, richness: 0.8 },
      iron: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  shipwreckCove: {
    id: 'shipwreckCove',
    name: 'Shipwreck Cove',
    icon: '⚓',
    description: 'A graveyard of ships. Dangerous reefs claimed many vessels... and their cargo.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a5a70',
    minAge: 2,
    spawnWeight: 6,
    entryEvents: [
      { id: 'eerie', weight: 25, text: 'Broken masts jut from the water like skeletal fingers.' },
      { id: 'salvage', weight: 30, text: 'You salvage cargo from a recent wreck!', rewards: { gold: 12, wood: 8 } },
      { id: 'treasure_chest', weight: 15, text: 'A locked chest washes ashore. Inside: treasure!', rewards: { gold: 15, gems: 1 } },
      { id: 'ghost_ship', weight: 15, text: 'A spectral ship materializes in the fog. You flee!', penalties: { food: 5, gold: 5 } },
      { id: 'riptide', weight: 15, text: 'A riptide pulls you under!', penalties: { gold: 8, food: 4 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 5, richness: 1.0 },
      wood: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  hauntedBattlefield: {
    id: 'hauntedBattlefield',
    name: 'Haunted Battlefield',
    icon: '⚔️',
    description: 'An ancient battlefield where the dead do not rest.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4045',
    minAge: 2,
    spawnWeight: 5,
    entryEvents: [
      { id: 'somber', weight: 20, text: 'Rusted weapons and bleached bones litter the ground.' },
      { id: 'relics', weight: 25, text: 'Ancient military relics! Historians would pay well.', rewards: { knowledge: 10, gold: 6 } },
      { id: 'armory', weight: 20, text: 'A collapsed armory yields preserved weapons.', rewards: { iron: 8, gold: 5 } },
      { id: 'ghost_soldiers', weight: 20, text: 'Spectral soldiers march through you!', penalties: { food: 7 } },
      { id: 'mass_grave', weight: 15, text: 'You disturb a mass grave.', penalties: { food: 5, gold: 5 } },
    ],
    gatherableResources: {
      iron: { baseAmount: 4, richness: 0.7 },
      knowledge: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  petrifiedForest: {
    id: 'petrifiedForest',
    name: 'Petrified Forest',
    icon: '🌲',
    description: 'An ancient forest turned to stone over millennia.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6a6058',
    minAge: 2,
    spawnWeight: 8,
    entryEvents: [
      { id: 'silence', weight: 30, text: 'Stone trees stand frozen in time. No birds sing.' },
      { id: 'petrified_wood', weight: 30, text: 'Petrified wood makes excellent building material!', rewards: { stone: 10, wood: 5 } },
      { id: 'fossil', weight: 15, text: 'Ancient fossils embedded in stone!', rewards: { knowledge: 8, stone: 4 } },
      { id: 'unstable', weight: 15, text: 'A stone tree topples!', penalties: { food: 5 } },
      { id: 'trapped', weight: 10, text: 'The stone forest is a maze. You wander for days.', penalties: { food: 8 } },
    ],
    gatherableResources: {
      stone: { baseAmount: 6, richness: 1.0 },
      knowledge: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  saltFlats: {
    id: 'saltFlats',
    name: 'Salt Flats',
    icon: '⚪',
    description: 'Blinding white plains of crystallized salt.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#d0d0d0',
    minAge: 2,
    spawnWeight: 7,
    entryEvents: [
      { id: 'blinding', weight: 25, text: 'The white expanse stretches forever.' },
      { id: 'harvest', weight: 35, text: 'High-quality salt! Worth its weight in gold.', rewards: { gold: 10 } },
      { id: 'caravan', weight: 15, text: 'A salt caravan shares their route.', rewards: { gold: 6, knowledge: 5 } },
      { id: 'sunburn', weight: 15, text: 'The reflected sun scorches you.', penalties: { food: 6 } },
      { id: 'lost', weight: 10, text: 'No landmarks. You wander in circles.', penalties: { food: 10 } },
    ],
    gatherableResources: { gold: { baseAmount: 4, richness: 0.8 } },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  coralReef: {
    id: 'coralReef',
    name: 'Coral Reef',
    icon: '🐠',
    description: 'A vibrant underwater garden teeming with colorful life.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#40a0a0',
    minAge: 3,
    spawnWeight: 6,
    requiresTech: 'sailing',
    entryEvents: [
      { id: 'beautiful', weight: 25, text: 'Colors beyond imagination! Fish dart through coral castles.' },
      { id: 'pearls', weight: 25, text: 'Giant clams yield lustrous pearls!', rewards: { gold: 12, gems: 1 } },
      { id: 'fishing', weight: 25, text: 'The reef teems with delicious fish.', rewards: { food: 15 } },
      { id: 'shark', weight: 15, text: 'A shark patrol! You escape with cuts.', penalties: { food: 6 } },
      { id: 'jellyfish', weight: 10, text: 'Jellyfish swarm!', penalties: { food: 8 } },
    ],
    gatherableResources: {
      food: { baseAmount: 6, richness: 1.0 },
      gold: { baseAmount: 4, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  dragonGraveyard: {
    id: 'dragonGraveyard',
    name: 'Dragon\'s Graveyard',
    icon: '🦴',
    description: 'Where ancient dragons came to die. Their bones still pulse with power.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a4540',
    minAge: 4,
    spawnWeight: 4,
    entryEvents: [
      { id: 'awe', weight: 20, text: 'Massive skeletal remains dwarf you.' },
      { id: 'dragonbone', weight: 25, text: 'Dragonbone! Stronger than steel.', rewards: { iron: 15, stone: 10 } },
      { id: 'hoard_remnants', weight: 20, text: 'Scattered remains of an ancient hoard!', rewards: { gold: 20, gems: 3 } },
      { id: 'residual_flame', weight: 20, text: 'Residual dragonfire erupts!', penalties: { food: 10 } },
      { id: 'guardian', weight: 15, text: 'A spectral dragon rises!', penalties: { gold: 10, food: 8 } },
    ],
    gatherableResources: {
      iron: { baseAmount: 6, richness: 1.0 },
      gold: { baseAmount: 5, richness: 0.8 },
      gems: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  titanRest: {
    id: 'titanRest',
    name: 'Titan\'s Rest',
    icon: '🦶',
    description: 'The fallen body of an impossibly ancient giant.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#6a5a50',
    minAge: 4,
    spawnWeight: 3,
    entryEvents: [
      { id: 'scale', weight: 20, text: 'You walk upon what was once a hand.' },
      { id: 'titan_ore', weight: 25, text: 'The titan\'s blood has become precious ore!', rewards: { iron: 18, gems: 2 } },
      { id: 'heart_chamber', weight: 20, text: 'Crystalized life essence!', rewards: { magic: 10, gems: 3 } },
      { id: 'parasites', weight: 20, text: 'Ancient parasites attack!', penalties: { food: 12 } },
      { id: 'collapse', weight: 15, text: 'The titan\'s form crumbles!', penalties: { food: 8, iron: 5 } },
    ],
    gatherableResources: {
      iron: { baseAmount: 8, richness: 1.2 },
      stone: { baseAmount: 6, richness: 0.8 },
      magic: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  sunkenCity: {
    id: 'sunkenCity',
    name: 'Sunken City',
    icon: '🏛️',
    description: 'A drowned metropolis beneath the waves.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#305070',
    minAge: 4,
    spawnWeight: 4,
    requiresTech: 'diving',
    entryEvents: [
      { id: 'wonder', weight: 20, text: 'Towers and temples rise from the seafloor.' },
      { id: 'treasury', weight: 25, text: 'The city treasury!', rewards: { gold: 25, gems: 2 } },
      { id: 'library', weight: 20, text: 'A preserved library!', rewards: { knowledge: 20 } },
      { id: 'guardian', weight: 20, text: 'An ancient guardian construct activates!', penalties: { food: 10, gold: 8 } },
      { id: 'pressure', weight: 15, text: 'The crushing depths take their toll.', penalties: { food: 12 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 8, richness: 1.0 },
      knowledge: { baseAmount: 6, richness: 0.8 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  phoenixNest: {
    id: 'phoenixNest',
    name: 'Phoenix Nest',
    icon: '🔥',
    description: 'A blazing aerie where phoenixes are reborn.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#a05020',
    minAge: 4,
    spawnWeight: 3,
    entryEvents: [
      { id: 'blazing', weight: 20, text: 'Eternal flames dance without fuel.' },
      { id: 'feathers', weight: 25, text: 'Phoenix feathers!', rewards: { magic: 12, gold: 10 } },
      { id: 'rebirth', weight: 15, text: 'You witness a phoenix rebirth!', rewards: { food: 20, magic: 8 } },
      { id: 'ash', weight: 25, text: 'Phoenix ash coats everything.', rewards: { magic: 6 }, penalties: { food: 8 } },
      { id: 'immolation', weight: 15, text: 'You\'re badly burned!', penalties: { food: 15 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 5, richness: 1.0 },
      gold: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  windsweptPeaks: {
    id: 'windsweptPeaks',
    name: 'Windswept Peaks',
    icon: '🌬️',
    description: 'Mountains so high the air itself becomes thin.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#8090a0',
    minAge: 4,
    spawnWeight: 5,
    entryEvents: [
      { id: 'thin_air', weight: 25, text: 'Each breath is a labor.' },
      { id: 'eagle_nest', weight: 25, text: 'A giant eagle\'s nest!', rewards: { food: 12, gold: 8 } },
      { id: 'sky_metal', weight: 20, text: 'Meteoric iron fallen from the heavens!', rewards: { iron: 15 } },
      { id: 'altitude', weight: 20, text: 'Altitude sickness strikes!', penalties: { food: 10 } },
      { id: 'blown_away', weight: 10, text: 'A gust hurls you from the path!', penalties: { food: 8, gold: 6 } },
    ],
    gatherableResources: {
      iron: { baseAmount: 6, richness: 0.9 },
      stone: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  primordialJungle: {
    id: 'primordialJungle',
    name: 'Primordial Jungle',
    icon: '🦎',
    description: 'A jungle untouched by time. Ancient creatures still roam here.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a5030',
    minAge: 5,
    spawnWeight: 4,
    entryEvents: [
      { id: 'ancient', weight: 20, text: 'Trees older than civilization tower overhead.' },
      { id: 'rare_herbs', weight: 25, text: 'Medicinal plants thought extinct!', rewards: { food: 15, knowledge: 10, gold: 8 } },
      { id: 'amber', weight: 20, text: 'Ancient amber containing preserved specimens!', rewards: { gems: 3, knowledge: 12 } },
      { id: 'predator', weight: 20, text: 'A massive predator hunts you!', penalties: { food: 12 } },
      { id: 'quicksand', weight: 15, text: 'The jungle floor swallows you!', penalties: { food: 10, wood: 5 } },
    ],
    gatherableResources: {
      food: { baseAmount: 8, richness: 1.2 },
      wood: { baseAmount: 7, richness: 1.0 },
      knowledge: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  obsidianFields: {
    id: 'obsidianFields',
    name: 'Obsidian Fields',
    icon: '🖤',
    description: 'Miles of volcanic glass, sharp as razors.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#202020',
    minAge: 5,
    spawnWeight: 5,
    entryEvents: [
      { id: 'glittering', weight: 25, text: 'The black glass reflects a thousand suns.' },
      { id: 'pure_obsidian', weight: 30, text: 'Flawless obsidian!', rewards: { stone: 15, gold: 10 } },
      { id: 'ancient_weapon', weight: 15, text: 'An obsidian blade from an ancient warrior.', rewards: { iron: 10, knowledge: 8 } },
      { id: 'cut', weight: 20, text: 'The razor edges slice through your boots.', penalties: { food: 8 } },
      { id: 'shatter', weight: 10, text: 'A field shatters beneath you!', penalties: { food: 12, gold: 5 } },
    ],
    gatherableResources: {
      stone: { baseAmount: 8, richness: 1.2 },
      iron: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  etherealMarsh: {
    id: 'etherealMarsh',
    name: 'Ethereal Marsh',
    icon: '🌫️',
    description: 'A swamp where the veil between worlds grows thin.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a5060',
    minAge: 6,
    spawnWeight: 4,
    entryEvents: [
      { id: 'liminal', weight: 20, text: 'The mist parts to reveal glimpses of other worlds.' },
      { id: 'spirit_gift', weight: 25, text: 'A benevolent spirit offers gifts!', rewards: { magic: 15, knowledge: 12 } },
      { id: 'lost_souls', weight: 20, text: 'Lost souls guide you to a cache.', rewards: { gold: 15, gems: 2 } },
      { id: 'possession', weight: 20, text: 'A spirit tries to claim your body!', penalties: { food: 12, magic: 5 } },
      { id: 'wrong_world', weight: 15, text: 'You step through the veil by accident.', penalties: { food: 15 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 6, richness: 1.0 },
      knowledge: { baseAmount: 4, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  celestialObservatory: {
    id: 'celestialObservatory',
    name: 'Celestial Observatory',
    icon: '🔭',
    description: 'Ancient towers built to study the stars.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#304080',
    minAge: 6,
    spawnWeight: 3,
    entryEvents: [
      { id: 'stars', weight: 20, text: 'Massive lenses focus starlight.' },
      { id: 'star_maps', weight: 30, text: 'Perfectly preserved star maps!', rewards: { knowledge: 25 } },
      { id: 'celestial_ore', weight: 20, text: 'Meteoric materials!', rewards: { iron: 12, magic: 8 } },
      { id: 'cosmic_truth', weight: 15, text: 'You glimpse a cosmic truth.', rewards: { knowledge: 15 }, penalties: { food: 8 } },
      { id: 'collapse', weight: 15, text: 'An ancient tower collapses!', penalties: { food: 10, knowledge: 5 } },
    ],
    gatherableResources: {
      knowledge: { baseAmount: 10, richness: 1.5 },
      iron: { baseAmount: 3, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  dragonsLair: {
    id: 'dragonsLair',
    name: 'Dragon\'s Lair',
    icon: '🐉',
    description: 'A living dragon guards treasure beyond imagining.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#602020',
    minAge: 6,
    spawnWeight: 2,
    entryEvents: [
      { id: 'terror', weight: 15, text: 'Massive claws gouge the stone floor.' },
      { id: 'hoard', weight: 20, text: 'The dragon sleeps! You fill your bags.', rewards: { gold: 40, gems: 5 } },
      { id: 'scale', weight: 20, text: 'Shed dragon scales!', rewards: { iron: 20, magic: 10 } },
      { id: 'awakened', weight: 25, text: 'THE DRAGON WAKES!', penalties: { food: 20, gold: 15 } },
      { id: 'tribute', weight: 20, text: 'The dragon demands tribute.', penalties: { gold: 25, gems: 3 }, rewards: { knowledge: 10 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 12, richness: 1.5 },
      gems: { baseAmount: 3, richness: 0.8 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  worldTreeRoot: {
    id: 'worldTreeRoot',
    name: 'World Tree Root',
    icon: '🌳',
    description: 'A root of the legendary World Tree.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#406040',
    minAge: 7,
    spawnWeight: 3,
    entryEvents: [
      { id: 'immense', weight: 20, text: 'The root is larger than most forests.' },
      { id: 'life_sap', weight: 25, text: 'Golden sap flows from the root!', rewards: { food: 30, magic: 15 } },
      { id: 'ancient_wisdom', weight: 25, text: 'The tree shares memories.', rewards: { knowledge: 30 } },
      { id: 'guardian_spirits', weight: 15, text: 'Nature spirits attack!', penalties: { food: 15, magic: 8 } },
      { id: 'overwhelming', weight: 15, text: 'The life energy is too intense!', penalties: { food: 12 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 8, richness: 1.2 },
      food: { baseAmount: 10, richness: 1.0 },
      knowledge: { baseAmount: 5, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  elementalConfluence: {
    id: 'elementalConfluence',
    name: 'Elemental Confluence',
    icon: '💫',
    description: 'Where fire, water, earth, and air collide.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#705090',
    minAge: 7,
    spawnWeight: 3,
    entryEvents: [
      { id: 'chaos', weight: 15, text: 'Elements war around you.' },
      { id: 'elemental_crystals', weight: 25, text: 'Crystallized elemental essence!', rewards: { magic: 20, gems: 4 } },
      { id: 'elemental_cores', weight: 20, text: 'You harvest elemental cores!', rewards: { magic: 15, iron: 12 } },
      { id: 'crossfire', weight: 25, text: 'Caught in elemental crossfire!', penalties: { food: 18, magic: 5 } },
      { id: 'unstable', weight: 15, text: 'Reality becomes unstable!', penalties: { food: 12, gold: 10 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 10, richness: 1.3 },
      gems: { baseAmount: 2, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  timeWornValley: {
    id: 'timeWornValley',
    name: 'Time-Worn Valley',
    icon: '⏳',
    description: 'Time flows strangely here.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#707050',
    minAge: 8,
    spawnWeight: 2,
    entryEvents: [
      { id: 'temporal', weight: 15, text: 'You see yourself walking ahead... and behind.' },
      { id: 'future_tech', weight: 25, text: 'Artifacts from a future that may never exist!', rewards: { knowledge: 35, iron: 15 } },
      { id: 'past_riches', weight: 25, text: 'You pluck treasures from the past!', rewards: { gold: 30, gems: 3 } },
      { id: 'aged', weight: 20, text: 'Time accelerates around you!', penalties: { food: 20 } },
      { id: 'paradox', weight: 15, text: 'A temporal paradox!', penalties: { food: 15, knowledge: 10 } },
    ],
    gatherableResources: {
      knowledge: { baseAmount: 12, richness: 1.5 },
      gold: { baseAmount: 8, richness: 1.0 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  abyssalTrench: {
    id: 'abyssalTrench',
    name: 'Abyssal Trench',
    icon: '🌊',
    description: 'The deepest darkness of the ocean.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#102030',
    minAge: 8,
    spawnWeight: 2,
    requiresTech: 'deep_diving',
    entryEvents: [
      { id: 'darkness', weight: 15, text: 'Light cannot reach here.' },
      { id: 'pressure_gems', weight: 25, text: 'Gems formed under crushing pressure!', rewards: { gems: 6, gold: 20 } },
      { id: 'ancient_tech', weight: 20, text: 'Technology from a sunken civilization!', rewards: { knowledge: 25, iron: 15 } },
      { id: 'leviathan', weight: 25, text: 'A leviathan notices you!', penalties: { food: 20, gold: 10 } },
      { id: 'crushing', weight: 15, text: 'The pressure is unbearable!', penalties: { food: 18, iron: 8 } },
    ],
    gatherableResources: {
      gems: { baseAmount: 4, richness: 1.0 },
      gold: { baseAmount: 8, richness: 1.0 },
      knowledge: { baseAmount: 6, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  dreamscape: {
    id: 'dreamscape',
    name: 'The Dreamscape',
    icon: '💭',
    description: 'A realm of pure imagination.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#8060a0',
    minAge: 8,
    spawnWeight: 2,
    entryEvents: [
      { id: 'surreal', weight: 15, text: 'Logic fails here.' },
      { id: 'manifest', weight: 25, text: 'You manifest your desires!', rewards: { magic: 25, gold: 20, food: 15 } },
      { id: 'inspiration', weight: 25, text: 'Pure inspiration floods your mind!', rewards: { knowledge: 40 } },
      { id: 'nightmare', weight: 20, text: 'Your fears manifest and attack!', penalties: { food: 18, magic: 10 } },
      { id: 'trapped', weight: 15, text: 'You become lost in dreams.', penalties: { food: 15, knowledge: 8 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 10, richness: 1.3 },
      knowledge: { baseAmount: 8, richness: 1.0 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  shadowRealm: {
    id: 'shadowRealm',
    name: 'Shadow Realm',
    icon: '🌑',
    description: 'A dark mirror of reality.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#1a1a2a',
    minAge: 9,
    spawnWeight: 2,
    entryEvents: [
      { id: 'mirror', weight: 15, text: 'Everything here is a twisted reflection.' },
      { id: 'shadow_essence', weight: 25, text: 'You harvest solidified shadows!', rewards: { magic: 30, crystals: 3 } },
      { id: 'dark_gold', weight: 20, text: 'Dark reflections of treasure.', rewards: { gold: 35 } },
      { id: 'doppelganger', weight: 25, text: 'Your shadow attacks!', penalties: { food: 20, magic: 8 } },
      { id: 'consumed', weight: 15, text: 'The darkness tries to consume you.', penalties: { food: 25 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 12, richness: 1.4 },
      crystals: { baseAmount: 2, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  astralPlane: {
    id: 'astralPlane',
    name: 'Astral Plane',
    icon: '✨',
    description: 'The realm between realms.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4050a0',
    minAge: 10,
    spawnWeight: 2,
    entryEvents: [
      { id: 'cosmic', weight: 15, text: 'You float among the stars.' },
      { id: 'energy', weight: 30, text: 'Pure cosmic energy coalesces!', rewards: { energy: 20, magic: 20 } },
      { id: 'cosmic_knowledge', weight: 25, text: 'The universe shares its secrets.', rewards: { knowledge: 50, energy: 10 } },
      { id: 'void_creature', weight: 18, text: 'Something notices you.', penalties: { food: 25, magic: 12 } },
      { id: 'lost', weight: 12, text: 'Without landmarks, you drift.', penalties: { food: 20, energy: 5 } },
    ],
    gatherableResources: {
      energy: { baseAmount: 8, richness: 1.5 },
      magic: { baseAmount: 8, richness: 1.0 },
      knowledge: { baseAmount: 10, richness: 1.0 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  primordialChaos: {
    id: 'primordialChaos',
    name: 'Primordial Chaos',
    icon: '🌀',
    description: 'The raw stuff of creation.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#503060',
    minAge: 10,
    spawnWeight: 2,
    entryEvents: [
      { id: 'creation', weight: 15, text: 'Reality itself is being born and dying.' },
      { id: 'raw_creation', weight: 25, text: 'You shape raw chaos!', rewards: { magic: 30, gems: 5, energy: 15 } },
      { id: 'impossible_material', weight: 20, text: 'Materials that shouldn\'t exist!', rewards: { iron: 25, magic: 20 } },
      { id: 'unmade', weight: 25, text: 'Chaos tries to unmake you!', penalties: { food: 30, magic: 15 } },
      { id: 'transformed', weight: 15, text: 'The chaos changes you.', rewards: { magic: 10 }, penalties: { food: 20, gold: 15 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 15, richness: 1.5 },
      energy: { baseAmount: 6, richness: 1.0 },
      gems: { baseAmount: 3, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  heavensGate: {
    id: 'heavensGate',
    name: 'Heaven\'s Gate',
    icon: '🌟',
    description: 'The threshold of the divine.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#d0d0ff',
    minAge: 10,
    spawnWeight: 1,
    entryEvents: [
      { id: 'divine', weight: 20, text: 'Light beyond light. Music beyond sound.' },
      { id: 'blessing', weight: 30, text: 'A divine blessing washes over you!', rewards: { magic: 40, energy: 25, food: 30 } },
      { id: 'revelation', weight: 25, text: 'Cosmic truths are revealed!', rewards: { knowledge: 60, energy: 15 } },
      { id: 'unworthy', weight: 15, text: 'You are judged... and found wanting.', penalties: { food: 25, magic: 15 } },
      { id: 'burned', weight: 10, text: 'Mortal eyes cannot bear this light!', penalties: { food: 30, knowledge: 10 } },
    ],
    gatherableResources: {
      energy: { baseAmount: 12, richness: 1.8 },
      magic: { baseAmount: 10, richness: 1.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  nightmareRealm: {
    id: 'nightmareRealm',
    name: 'Nightmare Realm',
    icon: '👁️',
    description: 'Where all fears converge.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#300020',
    minAge: 10,
    spawnWeight: 2,
    entryEvents: [
      { id: 'horror', weight: 15, text: 'Every fear you\'ve ever had watches you.' },
      { id: 'fear_essence', weight: 25, text: 'You harvest crystallized fear!', rewards: { magic: 35, crystals: 4 } },
      { id: 'nightmare_gold', weight: 20, text: 'Treasures beyond imagining.', rewards: { gold: 45, gems: 4 } },
      { id: 'confrontation', weight: 25, text: 'Your deepest fears manifest!', penalties: { food: 30, magic: 12 } },
      { id: 'maddening', weight: 15, text: 'The horrors defy comprehension.', penalties: { food: 25, knowledge: 15 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 14, richness: 1.5 },
      crystals: { baseAmount: 3, richness: 0.8 },
      gold: { baseAmount: 10, richness: 1.0 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  forgottenGod: {
    id: 'forgottenGod',
    name: 'Tomb of the Forgotten God',
    icon: '⚰️',
    description: 'A deity that outlived its worshippers.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a3040',
    minAge: 10,
    spawnWeight: 1,
    entryEvents: [
      { id: 'divine_corpse', weight: 15, text: 'The body of a god lies before you.' },
      { id: 'divine_essence', weight: 25, text: 'You absorb fragments of divine essence!', rewards: { magic: 50, energy: 30 } },
      { id: 'god_blood', weight: 20, text: 'Ichor of the divine!', rewards: { magic: 40, knowledge: 25 } },
      { id: 'divine_wrath', weight: 25, text: 'The god is not as dead as it seems!', penalties: { food: 35, magic: 20 } },
      { id: 'cursed', weight: 15, text: 'The god\'s dying curse latches onto you.', rewards: { magic: 15 }, penalties: { food: 30, gold: 20 } },
    ],
    gatherableResources: {
      magic: { baseAmount: 18, richness: 1.8 },
      energy: { baseAmount: 10, richness: 1.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  mirageCity: {
    id: 'mirageCity',
    name: 'Mirage City',
    icon: '🏜️',
    description: 'A city that flickers in and out of existence.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#a09070',
    minAge: 5,
    spawnWeight: 3,
    entryEvents: [
      { id: 'shimmer', weight: 20, text: 'Towers of gold shimmer on the horizon.' },
      { id: 'real', weight: 25, text: 'The city solidifies! You raid its treasures.', rewards: { gold: 25, gems: 2 } },
      { id: 'marketplace', weight: 20, text: 'Ghostly merchants offer impossible wares.', rewards: { gold: 15, knowledge: 12 } },
      { id: 'vanish', weight: 20, text: 'The city fades as you reach it!', penalties: { food: 10 } },
      { id: 'trapped', weight: 15, text: 'The city vanishes while you\'re inside!', penalties: { food: 15, gold: 8 } },
    ],
    gatherableResources: {
      gold: { baseAmount: 8, richness: 1.0 },
      knowledge: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  giantHive: {
    id: 'giantHive',
    name: 'Giant Insect Hive',
    icon: '🐝',
    description: 'A massive structure built by insects the size of horses.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#a08020',
    minAge: 3,
    spawnWeight: 4,
    entryEvents: [
      { id: 'buzzing', weight: 20, text: 'The droning is deafening.' },
      { id: 'royal_jelly', weight: 25, text: 'Royal jelly!', rewards: { food: 20, gold: 12 } },
      { id: 'honeycomb', weight: 25, text: 'Massive honeycombs drip with sweet gold!', rewards: { food: 18, gold: 8 } },
      { id: 'swarm', weight: 18, text: 'The swarm attacks!', penalties: { food: 12 } },
      { id: 'queen', weight: 12, text: 'You disturb the queen!', penalties: { food: 18, gold: 5 } },
    ],
    gatherableResources: {
      food: { baseAmount: 8, richness: 1.2 },
      gold: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  echoingCanyon: {
    id: 'echoingCanyon',
    name: 'Echoing Canyon',
    icon: '🏜️',
    description: 'A deep canyon where sound takes physical form.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#8a6050',
    minAge: 4,
    spawnWeight: 4,
    entryEvents: [
      { id: 'resonance', weight: 25, text: 'Your footsteps echo for minutes.' },
      { id: 'crystallized_sound', weight: 25, text: 'Sound has crystallized on the walls!', rewards: { gems: 3, magic: 8 } },
      { id: 'ancient_song', weight: 20, text: 'An ancient song plays on repeat.', rewards: { knowledge: 18 } },
      { id: 'avalanche', weight: 18, text: 'A shout triggers an avalanche!', penalties: { food: 10, stone: 5 } },
      { id: 'deafening', weight: 12, text: 'Echoes crescendo to unbearable levels!', penalties: { food: 12 } },
    ],
    gatherableResources: {
      gems: { baseAmount: 2, richness: 0.6 },
      stone: { baseAmount: 5, richness: 0.8 },
      knowledge: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  livingForest: {
    id: 'livingForest',
    name: 'The Living Forest',
    icon: '🌲',
    description: 'Every tree here is awake.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a4a2a',
    minAge: 5,
    spawnWeight: 4,
    entryEvents: [
      { id: 'aware', weight: 20, text: 'Eyes in the bark. The forest is AWARE.' },
      { id: 'gift', weight: 25, text: 'The forest deems you worthy.', rewards: { wood: 20, magic: 10 } },
      { id: 'sanctuary', weight: 20, text: 'The trees shelter you.', rewards: { food: 15, wood: 10 } },
      { id: 'judgement', weight: 20, text: 'The forest finds you wanting.', penalties: { food: 14 } },
      { id: 'lost', weight: 15, text: 'The trees move when you\'re not looking.', penalties: { food: 10 } },
    ],
    gatherableResources: {
      wood: { baseAmount: 8, richness: 1.2 },
      food: { baseAmount: 5, richness: 0.7 },
      magic: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  boneyard: {
    id: 'boneyard',
    name: 'The Boneyard',
    icon: '💀',
    description: 'Where countless creatures came to die.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#d0d0c0',
    minAge: 3,
    spawnWeight: 5,
    entryEvents: [
      { id: 'death', weight: 25, text: 'Bones crunch underfoot.' },
      { id: 'ivory', weight: 30, text: 'Pristine ivory tusks and horns!', rewards: { gold: 15, stone: 8 } },
      { id: 'fossils', weight: 20, text: 'Perfectly preserved fossils!', rewards: { knowledge: 15, gold: 10 } },
      { id: 'undead', weight: 15, text: 'Not all here are truly dead!', penalties: { food: 12 } },
      { id: 'curse', weight: 10, text: 'The weight of death is crushing.', penalties: { food: 10, magic: 5 } },
    ],
    gatherableResources: {
      stone: { baseAmount: 5, richness: 0.8 },
      gold: { baseAmount: 4, richness: 0.6 },
      knowledge: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },
};

// ============================================================================
// KINGDOM BIOMES (generated from kingdoms.js)
// ============================================================================

const KINGDOM_BIOMES = {};
for (const [id, kingdom] of Object.entries(KINGDOM_TYPES)) {
  KINGDOM_BIOMES[id] = {
    id,
    name: kingdom.name,
    icon: kingdom.icon,
    description: kingdom.description,
    buildingCategory: null,
    associatedBuildings: [],
    color: kingdom.color,
    minAge: kingdom.minAge,
    spawnWeight: kingdom.spawnWeight,
    isKingdomBiome: true,
    kingdomType: kingdom.type,
    kingdomTier: kingdom.tier,
    entryEvents: [
      {
        id: 'arrival',
        weight: 100,
        text: kingdom.flavorText
      },
    ],
    gatherableResources: {
      gold: { baseAmount: 8, richness: 1.2 },
      knowledge: { baseAmount: 4, richness: 0.8 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  };
}

// ============================================================================
// MERGED BIOMES EXPORT
// ============================================================================

export const BIOMES = {
  ...CORE_BIOMES,
  ...EXPANDED_BIOMES,
  ...DUNGEON_BIOMES,    // Dungeon biomes spawn dungeons instead of normal tiles
  ...ENCOUNTER_BIOMES,  // Encounter biomes spawn smaller combat areas (no level req)
  ...TOWER_BIOMES,      // Tower biomes spawn multi-floor dungeon variants
  ...KINGDOM_BIOMES,    // Kingdom biomes spawn kingdoms & city-states
  ...SHRINE_BIOMES,     // Shrine biomes grant one-time permanent bonuses
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getBiomeForBuilding(buildingId) {
  for (const biome of Object.values(BIOMES)) {
    if (biome.associatedBuildings && biome.associatedBuildings.includes(buildingId)) {
      return biome;
    }
  }
  return null;
}

export function getAllBiomeBuildings() {
  const allBuildings = new Set();
  for (const biome of Object.values(BIOMES)) {
    if (biome.associatedBuildings) {
      for (const buildingId of biome.associatedBuildings) {
        allBuildings.add(buildingId);
      }
    }
  }
  return allBuildings;
}

export function isUniversalBuilding(buildingId) {
  return getBiomeForBuilding(buildingId) === null;
}

export function getExpandedBiomes() {
  return Object.values(EXPANDED_BIOMES);
}

export function getBiomesByAge(age) {
  return Object.values(BIOMES).filter(biome => biome.minAge <= age);
}

export function getExpandedBiomesByAge(age) {
  return Object.values(EXPANDED_BIOMES).filter(biome => biome.minAge <= age);
}

export const BIOMES_EXPANDED = EXPANDED_BIOMES;
export { DUNGEON_BIOMES };
export { ENCOUNTER_BIOMES };
export { TOWER_BIOMES };
export { SHRINE_BIOMES };

export default BIOMES;
